import {Test, TestResult, Recommendation} from '../types/api';
import {HEALTH_CATEGORIES} from './healthCategories';

/**
 * Dummy Data Generation System
 * Creates realistic but obviously fake data for non-subscribed users
 */

// Sample test names for each category - realistic but generic
const SAMPLE_TEST_NAMES = {
    'heart-health': ['Total Cholesterol', 'HDL Cholesterol', 'LDL Cholesterol', 'Triglycerides'],
    'blood-health': ['White Blood Cells', 'Red Blood Cells', 'Hemoglobin', 'Platelets'],
    'liver-kidney-health': ['ALT', 'AST', 'Creatinine', 'BUN'],
    'metabolic-health': ['Glucose', 'HbA1c'],
    'thyroid-health': ['TSH', 'Free T4'],
    'hormone-health': ['Testosterone', 'Estradiol'],
    'vitamins-minerals': ['Vitamin D', 'Vitamin B12', 'Folate'],
    'bone-mineral-health': ['Calcium', 'Phosphorus'],
    'immune-allergy': ['Total IgE'],
    'mens-health': ['PSA'],
    'digestive-health': ['Amylase'],
};

// Reference ranges for common tests
const REFERENCE_RANGES = {
    'Total Cholesterol': '< 200 mg/dL',
    'HDL Cholesterol': '40-60 mg/dL',
    'LDL Cholesterol': '< 100 mg/dL',
    Triglycerides: '< 150 mg/dL',
    'White Blood Cells': '4.0-11.0 K/uL',
    'Red Blood Cells': '4.2-5.8 M/uL',
    Hemoglobin: '12.0-17.0 g/dL',
    Platelets: '150-450 K/uL',
    ALT: '7-35 U/L',
    AST: '8-40 U/L',
    Creatinine: '0.6-1.2 mg/dL',
    BUN: '7-25 mg/dL',
    Glucose: '70-100 mg/dL',
    HbA1c: '< 5.7 %',
    TSH: '0.4-4.0 mIU/L',
    'Free T4': '0.8-1.8 ng/dL',
    Testosterone: '300-1000 ng/dL',
    Estradiol: '30-300 pg/mL',
    'Vitamin D': '30-100 ng/mL',
    'Vitamin B12': '200-900 pg/mL',
    Folate: '2.7-17.0 ng/mL',
    Calcium: '8.5-10.5 mg/dL',
    Phosphorus: '2.5-4.5 mg/dL',
    'Total IgE': '< 150 IU/mL',
    PSA: '< 4.0 ng/mL',
    Amylase: '30-110 U/L',
};

/**
 * Generate a random value within a realistic range for a test
 */
const generateTestValue = (testName: string): string => {
    // Add more randomization to make values more obviously different on refresh
    const ranges = {
        'Total Cholesterol': () => (120 + Math.random() * 120).toFixed(0), // 120-240 (wider range)
        'HDL Cholesterol': () => (25 + Math.random() * 50).toFixed(0), // 25-75
        'LDL Cholesterol': () => (60 + Math.random() * 80).toFixed(0), // 60-140
        Triglycerides: () => (80 + Math.random() * 140).toFixed(0), // 80-220
        'White Blood Cells': () => (4.0 + Math.random() * 7.0).toFixed(1),
        'Red Blood Cells': () => (4.2 + Math.random() * 1.6).toFixed(1),
        Hemoglobin: () => (12.0 + Math.random() * 5.0).toFixed(1),
        Platelets: () => (150 + Math.random() * 300).toFixed(0),
        ALT: () => (15 + Math.random() * 25).toFixed(0),
        AST: () => (20 + Math.random() * 25).toFixed(0),
        Creatinine: () => (0.7 + Math.random() * 0.4).toFixed(1),
        BUN: () => (10 + Math.random() * 20).toFixed(0),
        Glucose: () => (80 + Math.random() * 30).toFixed(0),
        HbA1c: () => (4.5 + Math.random() * 1.5).toFixed(1),
        TSH: () => (1.0 + Math.random() * 2.0).toFixed(1),
        'Free T4': () => (1.0 + Math.random() * 0.6).toFixed(1),
        Testosterone: () => (400 + Math.random() * 400).toFixed(0),
        Estradiol: () => (50 + Math.random() * 200).toFixed(0),
        'Vitamin D': () => (25 + Math.random() * 50).toFixed(0),
        'Vitamin B12': () => (300 + Math.random() * 400).toFixed(0),
        Folate: () => (5 + Math.random() * 10).toFixed(1),
        Calcium: () => (9.0 + Math.random() * 1.0).toFixed(1),
        Phosphorus: () => (3.0 + Math.random() * 1.0).toFixed(1),
        'Total IgE': () => (20 + Math.random() * 80).toFixed(0),
        PSA: () => (1.0 + Math.random() * 2.0).toFixed(1),
        Amylase: () => (40 + Math.random() * 50).toFixed(0),
    };

    const generator = ranges[testName as keyof typeof ranges];
    return generator ? generator() : (Math.random() * 100).toFixed(1);
};

/**
 * Generate dummy test results for a specific category
 */
const generateCategoryResults = (categoryKey: string): TestResult[] => {
    const testNames = SAMPLE_TEST_NAMES[categoryKey as keyof typeof SAMPLE_TEST_NAMES] || [];

    return testNames.map((testName) => ({
        test_name: testName,
        test_code: testName.replace(/\s+/g, '').toUpperCase(),
        result: generateTestValue(testName),
        refRange: REFERENCE_RANGES[testName as keyof typeof REFERENCE_RANGES] || 'Normal',
        panel_name: HEALTH_CATEGORIES[categoryKey]?.name || 'General Health',
        panel_code: categoryKey.toUpperCase(),
        units: getUnitsForTest(testName),
        notes: '',
        abnormal: Math.random() > 0.8 ? (Math.random() > 0.5 ? 'H' : 'L') : '', // 20% chance of abnormal
        statusDate: new Date().toISOString(),
    }));
};

/**
 * Get appropriate units for a test
 */
const getUnitsForTest = (testName: string): string => {
    const unitMap = {
        'Total Cholesterol': 'mg/dL',
        'HDL Cholesterol': 'mg/dL',
        'LDL Cholesterol': 'mg/dL',
        Triglycerides: 'mg/dL',
        'White Blood Cells': 'K/uL',
        'Red Blood Cells': 'M/uL',
        Hemoglobin: 'g/dL',
        Platelets: 'K/uL',
        ALT: 'U/L',
        AST: 'U/L',
        Creatinine: 'mg/dL',
        BUN: 'mg/dL',
        Glucose: 'mg/dL',
        HbA1c: '%',
        TSH: 'mIU/L',
        'Free T4': 'ng/dL',
        Testosterone: 'ng/dL',
        Estradiol: 'pg/mL',
        'Vitamin D': 'ng/mL',
        'Vitamin B12': 'pg/mL',
        Folate: 'ng/mL',
        Calcium: 'mg/dL',
        Phosphorus: 'mg/dL',
        'Total IgE': 'IU/mL',
        PSA: 'ng/mL',
        Amylase: 'U/L',
    };

    return unitMap[testName as keyof typeof unitMap] || '';
};

/**
 * Generate dummy recommendations
 */
export const generateDummyRecommendations = (): Recommendation[] => {
    return [
        {
            Id: 1,
            name: 'Sample Heart Health Support',
            enabled: true,
            content: '<p>This is a sample recommendation for demonstration purposes only.</p>',
            image: null,
            sup_type: 'supplement',
            price: 49.99,
            members_price: 39.99,
            url: 'https://example.com/sample-product',
            biomarkers: [],
        },
        {
            Id: 2,
            name: 'Sample Vitamin D Support',
            enabled: true,
            content: '<p>This is a sample vitamin recommendation for demonstration purposes only.</p>',
            image: null,
            sup_type: 'vitamin',
            price: 24.99,
            members_price: 19.99,
            url: 'https://example.com/sample-vitamin',
            biomarkers: [],
        },
    ];
};

/**
 * Generate dummy category scores with more randomization
 */
export const generateDummyCategoryScores = (): Record<string, number> => {
    return {
        'Heart Health': Math.round(30 + Math.random() * 60), // 30-90 - wider range
        'Blood Health': Math.round(40 + Math.random() * 50), // 40-90
        'Liver & Kidney': Math.round(35 + Math.random() * 55), // 35-90
        'Metabolic Health': Math.round(25 + Math.random() * 65), // 25-90
        'Thyroid Health': Math.round(45 + Math.random() * 45), // 45-90
        'Hormone Health': Math.round(30 + Math.random() * 60), // 30-90
        'Vitamins & Minerals': Math.round(20 + Math.random() * 70), // 20-90
        'Bone & Mineral Health': Math.round(50 + Math.random() * 40), // 50-90
        'Immune & Allergy': Math.round(60 + Math.random() * 30), // 60-90
    };
};

/**
 * Generate a complete dummy test dataset
 */
export const generateDummyTests = (patientName = {firstName: 'Sample', lastName: 'Patient'}): Test[] => {
    const baseDate = new Date();
    baseDate.setMonth(baseDate.getMonth() - 1); // 1 month ago

    // Generate 2-4 tests with different dates (randomized)
    const tests: Test[] = [];
    const numTests = 2 + Math.floor(Math.random() * 3); // 2-4 tests

    for (let i = 0; i < numTests; i++) {
        const testDate = new Date(baseDate);
        testDate.setDate(testDate.getDate() - i * 30); // Space tests 30 days apart

        // Select 1-4 random categories for each test (more variation)
        const categoryKeys = Object.keys(SAMPLE_TEST_NAMES);
        const numCategories = 1 + Math.floor(Math.random() * 4); // 1-4 categories
        const selectedCategories = categoryKeys.sort(() => Math.random() - 0.5).slice(0, numCategories);

        const allResults: TestResult[] = [];
        selectedCategories.forEach((categoryKey) => {
            allResults.push(...generateCategoryResults(categoryKey));
        });

        // Choose a random resultType to show different views
        const resultTypes = ['blood', 'allergy', 'blood', 'blood']; // Favor blood for more variety
        const randomResultType = resultTypes[Math.floor(Math.random() * resultTypes.length)];

        tests.push({
            id: 999990 + i, // Use high IDs to avoid conflicts
            elis_id: 999990 + i,
            oid: `SAMPLE${String(999990 + i).padStart(8, '0')}`,
            createdDate: testDate.toISOString(),
            statusDate: testDate.toISOString(),
            amendedDate: testDate.toISOString(),
            status: 'Resulted',
            resulted: '1',
            final: true,
            issueCount: 0,
            issues: '',
            customer_id: 99999,
            firstName: patientName.firstName,
            lastName: patientName.lastName,
            birthdate: '1990-01-01T00:00:00.000Z',
            sent: true,
            appointmentDate: '',
            appointmentLocation: '',
            appointmentType: [null, null], // Required for getNameString function
            appointmentDuration: '',
            appointmentCalendar: '',
            test_type: [null, null], // Required for getNameString function
            name: null, // Required field from real data
            resultType: randomResultType, // Add resultType to trigger panel views
            results: allResults,
            recommendations: i === 0 ? generateDummyRecommendations() : [], // Only add recommendations to most recent test
        });
    }

    return tests;
};

/**
 * Generate dummy data response matching API structure
 */
export const generateDummyDataResponse = (patientName?: {firstName: string; lastName: string}) => {
    return {
        orders: generateDummyTests(patientName),
        categoryScores: generateDummyCategoryScores(),
        subscription: false, // Always false for dummy data
    };
};

export default {
    generateDummyTests,
    generateDummyRecommendations,
    generateDummyCategoryScores,
    generateDummyDataResponse,
};
